// ============================================================
// navEntries.js
// The portal's navigation model, extracted so the desktop rail
// (SideNav) and the mobile top bar (MobileTopBar) read from ONE
// source. Two surfaces each keeping their own copy of the entry
// list is how they drift.
//
// `badgeKey` names a count in the object returned by
// useAdminBadgeCounts. Only entries that carry one show a badge.
// ============================================================

import {
  Building2, Briefcase, Users, Send, Rocket,
  ListTree, GraduationCap, ClipboardCheck, BellRing, BarChart3, Network, Receipt,
  MessageSquare, Megaphone, MailCheck, FileX, FileSpreadsheet,
} from 'lucide-react';

export const NAV_ENTRIES = [
  { path: '/tenants',          label: 'Tenants',          icon: Building2 },
  { path: '/clients',          label: 'Clients',          icon: Briefcase },
  { path: '/users',            label: 'Users',            icon: Users },
  { path: '/invitations',      label: 'Invitations',      icon: Send },
  { path: '/trial-requests',   label: 'Trial Requests',   icon: Rocket,        badgeKey: 'trialNew' },
  { path: '/contact-messages', label: 'Contact Messages', icon: MessageSquare, badgeKey: 'contactNew' },
];

// Phase 2 Admin UI session — Platform Management group.
export const PLATFORM_MGMT_ENTRIES = [
  { path: '/platform/billing',                label: 'Billing',                icon: Receipt },
  { path: '/platform/billing-summary',        label: 'Billing Summary',        icon: FileSpreadsheet },
  { path: '/platform/event-types',            label: 'Event Types',            icon: ListTree },
  { path: '/platform/training-types',         label: 'Training Types',         icon: GraduationCap },
  { path: '/platform/audit-templates',        label: 'Audit Templates',        icon: ClipboardCheck },
  { path: '/platform/notification-types',     label: 'Notification Types',     icon: BellRing },
  { path: '/platform/operator-notifications', label: 'Operator Notifications', icon: BellRing },
  { path: '/platform/service-notifications',  label: 'Service Notifications',  icon: Megaphone },
  { path: '/platform/notification-log',       label: 'Notification Delivery',  icon: MailCheck },
  { path: '/platform/deletion-requests',      label: 'Deletion Requests',      icon: FileX },
  { path: '/platform/tenant-integrations',    label: 'Tenant Integrations',    icon: Network },
];

// Platform Analytics group — internal A2Z IT tooling.
export const PLATFORM_ANALYTICS_ENTRIES = [
  { path: '/platform/analytics', label: 'Analytics', icon: BarChart3 },
];

export const ALL_NAV_ENTRIES = [
  ...NAV_ENTRIES,
  ...PLATFORM_MGMT_ENTRIES,
  ...PLATFORM_ANALYTICS_ENTRIES,
];

// Active-state rule, shared by the rail and the top bar: an entry owns
// its own path and everything beneath it, so /clients/123 still lights
// up "Clients".
export function isEntryActive(entryPath, pathname) {
  return pathname === entryPath || pathname.startsWith(`${entryPath}/`);
}

// The label for the current route, used as the mobile top-bar title.
//
// Sorted longest-path-first because the platform paths genuinely nest:
// a plain `find` would let /platform/notification-types match a prefix
// of /platform/notification-log and title the wrong page. Falls back to
// 'Admin' rather than an empty bar on an unrecognised route (the
// invitation-detail and client-detail routes are not nav entries).
const BY_SPECIFICITY = [...ALL_NAV_ENTRIES].sort((a, b) => b.path.length - a.path.length);

export function titleForPath(pathname) {
  const match = BY_SPECIFICITY.find((e) => isEntryActive(e.path, pathname));
  return match ? match.label : 'Admin';
}
